import { Command } from 'cmdk'

import {
  Checkbox,
  DropdownMenu,
  Input,
  SearchIcon,
  Tag,
  TagIcon,
} from '@buffer-mono/popcorn'

import { useTagsQuery } from '~publish/hooks/useTags'

import styles from './TagFilterSubMenu.module.css'

import type { JSX } from 'react'

export type TagFilterSubMenuProps = {
  selectedTags: string[]
  onTagSelect: (tagIds: string[]) => void
}

/**
 * The searchable, multi-select tag list. Rendered on its own inside a
 * `DropdownMenu.Content` (so it becomes a bottom-sheet drawer on narrow
 * viewports) or nested in a filter dropdown's "Filter by tag" submenu.
 */
export function TagFilterList({
  selectedTags,
  onTagSelect,
}: TagFilterSubMenuProps): JSX.Element {
  const { tags } = useTagsQuery()
  const toggleTag = (tagId: string): void => {
    const newSelection = selectedTags.includes(tagId)
      ? selectedTags.filter((id) => id !== tagId)
      : [...selectedTags, tagId]
    onTagSelect(newSelection)
  }

  return (
    <Command loop className={styles.command}>
      {tags.length > 0 && (
        <Command.Input asChild>
          <Input
            aria-label="Search tags"
            prefix={<SearchIcon />}
            placeholder="Search tags..."
            className={styles.searchInput}
          />
        </Command.Input>
      )}
      {/* Guard the results list, not the whole command: the search input stays
          translatable while cmdk's churning rows (tag names, empty state) are
          protected from the translate-unmount crash. See FilterByTag. */}
      <Command.List className={styles.list} translate="no">
        <Command.Empty className={styles.empty}>
          {tags.length === 0 ? 'No tags available' : 'No tags found'}
        </Command.Empty>
        {tags.map((tag) => (
          <Command.Item
            key={tag.id}
            value={tag.name}
            onSelect={(): void => toggleTag(tag.id)}
            className={styles.item}
            aria-label={`Toggle ${tag.name} filter`}
          >
            <Checkbox checked={selectedTags.includes(tag.id)} tabIndex={-1} />
            <Tag color={tag.color}>{tag.name}</Tag>
          </Command.Item>
        ))}
      </Command.List>
    </Command>
  )
}

export function TagFilterSubMenu(props: TagFilterSubMenuProps): JSX.Element {
  return (
    <DropdownMenu.Sub>
      <DropdownMenu.SubTrigger>
        <TagIcon /> Filter by tag
      </DropdownMenu.SubTrigger>
      <DropdownMenu.SubMenu>
        <TagFilterList {...props} />
      </DropdownMenu.SubMenu>
    </DropdownMenu.Sub>
  )
}
